import { Link, Outlet, useLocation } from "react-router-dom";
import { Badge, Button, Container, Nav, Navbar } from "react-bootstrap";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { setHideFilter } from "../../redux/filtersSlice";
import { RootState } from "../../redux/store";
import FiltersContainer from "../Filters/components/FiltersContainer";
import { useFiltersImprovementPlans } from "../../features/improvementPlan/hooks/useFiltersImprovementPlans";
import useExportToPDF from "../../hooks/useExportToPDF";
import { addAuditLog } from "../../features/auditLogs/api/auditApi";
import { AuditActionEnum } from "../../features/auditLogs/types";
import { ApiErrorResponse } from "../../types/ApiErrorResponse";
import ModalGlobal from "../AppModal/ModalGlobal";
import { useAlert } from "../../hooks/useAlert";
import AlertMessage from "../AlertMessage";


export type DashboardImprovementPlansOutletContext = {
    section1Ref: React.RefObject<HTMLDivElement>;
    section2Ref: React.RefObject<HTMLDivElement>;
};
export default function ImprovementPlansDashboardLayout() {
    const { t } = useTranslation('improvementPlan');
    const location = useLocation();
    const dispatch = useDispatch();
    const { alert, hideAlert, showAlert, isVisible } = useAlert({ fromLocation: false });
    const { data: filters } = useFiltersImprovementPlans();

    const MENU_ITEMS = [
        { name: "Dashboard", link: "/improvement-plans/dashboard", notifications: undefined },
        { name: t("improvementPlan:menu.history"), link: "/improvement-plans/historic", notifications: undefined },
        {name: t("improvementPlan:menu.gantt"), link: "/improvement-plans/gantt", notifications: undefined}
    ];

    useEffect(() => {
        const currentPath = location.pathname;
        const matchingItem = MENU_ITEMS.find((item) => currentPath === item.link);
        if (matchingItem) {
            setSelectedItem(matchingItem.link);
        } else {
            setSelectedItem("/improvement-plans/dashboard");
        }
    }, [location.pathname]);

    const [selectedItem, setSelectedItem] = useState<string>("/improvement-plans/historic");

    const [isFilterVisible, setIsFilterVisible] = useState<boolean>(false);
    const isHideFilter = useSelector((state: RootState) => state.filters.improvementPlans.hideFilter);

    const { exportToPDF, isExporting, section1Ref, section2Ref } = useExportToPDF(t, 'improvementPlans', filters);

    const [showModal, setShowModal] = useState(false);
    const handleOpen = () => setShowModal(true);
    const handleClose = () => setShowModal(false);


    const handleExportPdf = async () => {
        try {
            if (section1Ref.current && section2Ref.current) {
                exportToPDF();
                await addAuditLog({
                    entity: 'improvementPlanDashboard',
                    action: AuditActionEnum.Print,
                    source: window.location.pathname,
                    description: "Full improvement plans dashboard report printed"
                });
                handleClose();
            }
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            const errorMessage = fetchError?.message

            showAlert({
                message: errorMessage?.toString() ?? t("common:modalPrint.errorToGeneratePdf"),
                type: 'danger',
                duration: 8000
            });
        }
    }

    const handleClick = () => {
        dispatch(setHideFilter({ table: 'improvementPlans', hide: false }));  
        setIsFilterVisible(!isFilterVisible); 
    };

    return (
        <Container fluid>
            {isVisible && (
                <AlertMessage
                    message={alert!.message}
                    type={alert!.type}
                    onClose={hideAlert}
                />
            )}
            <ModalGlobal
                show={showModal}
                onClose={handleClose}
                title={t("common:modalPrint.modalShowHistoricalConfirmationPDFTitle")}
                subtitle={t("common:modalPrint.modalShowHistoricalConfirmationPDFDescription", { description: "" })}
                body={<div></div>}
                onSave={() => {
                    handleExportPdf();
                }}
                contentType='info'
                saveText={t("common:modalActionOK")}
                closeText={t("common:modalActionNOTOK")}
                isLoading={false}
            /> 
            <Navbar bg="white" expand="lg">
                <Navbar.Toggle aria-controls="basic-navbar-nav" />
                <Navbar.Collapse id="basic-navbar-nav">
                    <Nav className="me-auto w-90" variant="tabs" activeKey={MENU_ITEMS.findIndex(item => item.link === selectedItem)}>
                        {MENU_ITEMS.map((item, index) => (
                            <Nav.Link
                                as={Link}
                                to={item.link}
                                key={index}
                                eventKey={index}
                                onClick={() => setSelectedItem(item.link)}
                                className={`position-relative survey-tab-link ${item.link === selectedItem ? "active-tab" : ""}`}
                            >
                                {item.name}
                                {!!item.notifications &&
                                    <Badge bg="success" className="position-absolute top-0 translate-middle badge rounded-pill">
                                        {item.notifications < 10 ? item.notifications : "+9"}
                                    </Badge>
                                }
                            </Nav.Link>
                        ))}
                    </Nav>
                    <Nav className="ms-auto">
                        <Button
                            variant=""
                            onClick={(e) => {
                                e.preventDefault();
                                handleOpen();
                            }}
                            aria-controls="collapse-filter-id"
                            style={{
                                border: 0,
                                visibility: (selectedItem === "/improvement-plans/dashboard") ? 'visible' : 'hidden'
                            }}
                        >
                            <img
                                src="/download.png"
                                alt={isExporting ? 'Generant PDF...' : t(" ButtonExportTitle")}
                                style={{ width: '50px', }}
                            />
                        </Button>
                        <Button variant="" onClick={handleClick} aria-controls="collapse-filter-id" style={{ border: 0 }}>
                            <img
                                src="/filter.png"
                                alt="Filtar"
                                style={{ width: '50px' }}
                            />
                        </Button>
                    </Nav>

                </Navbar.Collapse>
            </Navbar>

            <div>
                {!isHideFilter && filters  && (
                   <div className="dashboard-section">
                        <FiltersContainer
                            filters={filters}
                            isFilterVisible={isFilterVisible && !isHideFilter}
                            tableKey="improvementPlans"
                            historicalKey="improvementPlans"
                        />
                    </div>
                )}
            </div>
            <div className="justify-content-center p-0">
                <Outlet context={{ section1Ref, section2Ref }} />
            </div>
        </Container>
    );
}